/**
 * FetchRunHistory — the monitor's recent fetch runs.
 * Template example: NextAdmin `tables/basic-tables/_component/leads-report` (compact table in a card).
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { listIntelSourceMonitorFetchRunsOptions } from '../../../generated/api/@tanstack/react-query.gen.js';
import type { IntelSourceFetchRun } from '../../../generated/api/types.gen.js';
import { Badge } from '../../../components/ui/Badge.js';
import { Button } from '../../../components/ui/Button.js';
import { Card } from '../../../components/ui/Card.js';
import { DataTable } from '../../../components/ui/DataTable.js';
import type { DataTableColumn } from '../../../components/ui/DataTable.js';
import { EmptyState } from '../../../components/ui/EmptyState.js';
import { Callout } from '../../../components/ui/Callout.js';
import { errorLabel, formatRelativeTime } from './SourceCard.js';

interface FetchRunHistoryProps {
  /** Workflow instance id of the monitor whose fetch runs to show. */
  workflowInstanceId: number;
}

type RunStatus = IntelSourceFetchRun['status'];

const ACTIVE_STATUSES: RunStatus[] = ['queued', 'dispatched', 'running'];

const STATUS_LABEL: Record<RunStatus, string> = {
  queued: 'Queued',
  dispatched: 'Dispatched',
  running: 'Running',
  succeeded: 'Succeeded',
  partial: 'Partial',
  failed: 'Failed',
  cancelled: 'Cancelled',
  skipped: 'Skipped',
};

function statusVariant(status: RunStatus): 'success' | 'warning' | 'danger' | 'neutral' {
  switch (status) {
    case 'succeeded':
      return 'success';
    case 'partial':
      return 'warning';
    case 'failed':
      return 'danger';
    default:
      return 'neutral';
  }
}

function RunResult({ run }: { run: IntelSourceFetchRun }) {
  return (
    <span className="text-muted">
      {run.itemsNew > 0 && <span className="mr-2 text-body">+{run.itemsNew} new</span>}
      {run.itemsListed > 0 && <span className="mr-2">{run.itemsListed} listed</span>}
      {run.itemsDuplicate > 0 && <span className="mr-2">{run.itemsDuplicate} duplicates</span>}
      {run.quotaUnitsUsed > 0 && <span className="mr-2">{run.quotaUnitsUsed} quota</span>}
      {run.workflowRunId != null && (
        <Link to={`/agents/runs/${run.workflowRunId}`} className="mr-2 text-accent-text hover:underline">
          Run {run.workflowRunId}
        </Link>
      )}
      {run.errorCode && (
        <span className="text-danger" title={run.errorDetail ?? undefined}>
          {errorLabel(run.errorCode)}
          {run.errorDetail ? `: ${run.errorDetail.slice(0, 60)}${run.errorDetail.length > 60 ? '…' : ''}` : ''}
        </span>
      )}
    </span>
  );
}

const COLUMNS: DataTableColumn<IntelSourceFetchRun>[] = [
  {
    key: 'status',
    header: 'Status',
    render: (run) => (
      <Badge variant={statusVariant(run.status)} dot={ACTIVE_STATUSES.includes(run.status)}>
        {STATUS_LABEL[run.status] ?? run.status}
      </Badge>
    ),
  },
  { key: 'trigger', header: 'Trigger', render: (run) => <span className="capitalize text-muted">{run.trigger}</span> },
  { key: 'result', header: 'Result', render: (run) => <RunResult run={run} /> },
  {
    key: 'when',
    header: 'Started',
    align: 'right',
    render: (run) => (
      <span className="whitespace-nowrap text-muted">{formatRelativeTime(run.startedAt ?? run.createdAt)}</span>
    ),
  },
];

export const FetchRunHistory: React.FC<FetchRunHistoryProps> = ({ workflowInstanceId }) => {
  const { data, isLoading, isError, isFetching, refetch } = useQuery({
    ...listIntelSourceMonitorFetchRunsOptions({ path: { workflowInstanceId }, query: { limit: 20 } }),
    staleTime: 10_000,
    refetchInterval: (q) => {
      const runs = q.state.data?.data ?? [];
      return runs.some((r) => ACTIVE_STATUSES.includes(r.status)) ? 10_000 : false;
    },
  });
  const runs = data?.data ?? [];

  return (
    <Card
      title="Fetch history"
      actions={
        <Button
          iconOnly
          variant="ghost"
          size="sm"
          aria-label="Refresh fetch history"
          loading={isFetching}
          onClick={() => refetch()}
        >
          <RefreshCw size={14} aria-hidden="true" />
        </Button>
      }
    >
      {isError ? (
        <Callout
          tone="danger"
          title="Could not load the fetch history"
          actions={
            <Button size="sm" variant="secondary" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          Check your connection and try again.
        </Callout>
      ) : (
        <DataTable
          columns={COLUMNS}
          rows={runs}
          rowKey={(run) => run.id}
          loading={isLoading}
          aria-label="Fetch runs"
          empty={
            <EmptyState
              icon={RefreshCw}
              title="No fetch runs yet"
              description="Runs appear here once the monitor fetches content, on its schedule or with Fetch now."
            />
          }
        />
      )}
    </Card>
  );
};
